import { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../../shared/makeApiCall";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import AddInventoryForm from "../../../components/forms/setup/product/AddInventoryForm";
import QbProductLookupPanel from "../../../components/forms/setup/product/QbProductLookupPanel";
import ImportMediaCsvDropzone from "../../../components/importMedia/ImportMediaCsvDropzone";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";

export function ProductsViewModel(logout, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({
    data: [],
  });

  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [qbLookupLoadingProductId, setQbLookupLoadingProductId] =
    useState(null);
  const [productsForDropdown, setProductsForDropdown] = useState([]);

  const fetchProductsForDropdown = async function (search, page) {
    let callResult = await makePostApiCall(`fetchProductsForDropdown`);
    if (callResult.success) {
      setProductsForDropdown(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProducts = async function (search, page) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(`products`, payload);
    setLoading(false);
    if (callResult.success) {
      setProducts(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const deleteProduct = async function (id, index) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteProduct/${id}`, {});
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts(null, 0);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeleteProduct = function (product, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteProduct(product.id, index)}
          message={`Are you sure you want to delete ${product.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const uploadProductsCsv = async function (uploadedFile) {
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "uploadProductsCsv",
      payload,
      "multipart/form-data",
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const addInventoryApiCall = async function (values) {
    let callResult = await makePostApiCall(
      "addManufacturedProductInventory",
      values,
    );

    if (callResult.success) {
      setSlideOver({
        childComponent: null,
        open: false,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openImportProduct = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="productsCsv"
          actionCall={uploadProductsCsv}
          setOpen={setEmptyModelOpen}
          title="Products"
        />
      ),
      open: true,
    });
  };

  const addInventory = () => {
    setSlideOver({
      childComponent: (
        <AddInventoryForm
          actionCall={addInventoryApiCall}
          productOptions={productsForDropdown}
          setOpen={setSlideOver}
        />
      ),
      open: true,
    });
  };

  const fetchProductFromQb = async function (quickbooksId) {
    return makeGetApiCall(`quickbooks/fetchProductFromQb/${quickbooksId}`);
  };

  const openQbLookup = async function (product) {
    if (!product.quickbooks_id) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "This product has no QuickBooks ID.",
          SnackbarType.Error,
        ),
      );
      return;
    }

    setQbLookupLoadingProductId(product.id);
    try {
      const callResult = await fetchProductFromQb(product.quickbooks_id);

      if (callResult.success) {
        const qbProduct = callResult.data?.data ?? callResult.data;
        setSlideOver({
          childComponent: (
            <QbProductLookupPanel product={qbProduct} setOpen={setSlideOver} />
          ),
          open: true,
        });
      } else {
        const errorMessage =
          callResult.data?.subtitle ??
          callResult.data?.message ??
          "Failed to fetch QuickBooks product.";
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            errorMessage,
            SnackbarType.Error,
          ),
        );
      }
    } finally {
      setQbLookupLoadingProductId(null);
    }
  };

  return {
    askDeleteProduct,
    emptyModal,
    setEmptyModelOpen,
    getProducts,
    loading,
    products,
    setSlideOver,
    slideOver,
    openImportProduct,
    addInventory,
    fetchProductsForDropdown,
    openQbLookup,
    qbLookupLoadingProductId,
  };
}
